import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, TextField, Button, Alert,
  IconButton, InputAdornment, Switch, Chip, Tooltip, Link,
  Tabs, Tab, FormControl, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Link as RouterLink } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Save as SaveIcon, Visibility, VisibilityOff,
  Laptop as LaptopIcon, PhoneAndroid as PhoneIcon,
  Tablet as TabletIcon, DevicesOther as DevicesOtherIcon,
  ShieldOutlined, VerifiedUserOutlined, ContentCopy,
  LocationOnOutlined, FiberManualRecord as DotIcon,
  Login as LoginIcon, Logout as LogoutIcon, Block as BlockIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/api/endpoints/auth';
import { portalApi } from '@/api/endpoints/hr';
import { auditApi, LoginHistoryEntry } from '@/api/endpoints/audit';
import { useNotificationStore } from '@/stores/notificationStore';
import { useUIStore } from '@/stores/uiStore';
import { extractErrorMessage, formatDateTime } from '@/utils/format';

interface SettingsTabProps {
  user: any;
  emp: any;
}

export default function SettingsTab({ user, emp }: SettingsTabProps) {
  const [subTab, setSubTab] = useState(0);

  return (
    <Box>
      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{
          mb: 2.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.sm,
            minHeight: 36, py: 0.5, px: 2, borderRadius: '8px', mr: 1,
          },
          '& .Mui-selected': { bgcolor: 'primary.main', color: 'white !important' },
          '& .MuiTabs-indicator': { display: 'none' },
        }}
      >
        <Tab label="Notifications" />
        <Tab label="Security" />
        <Tab label="Preferences" />
      </Tabs>

      {subTab === 0 && <NotificationsSubTab emp={emp} />}
      {subTab === 1 && <SecuritySubTab user={user} />}
      {subTab === 2 && <PreferencesSubTab />}
    </Box>
  );
}

// ── Notifications Sub-Tab ──
function NotificationsSubTab({ emp }: { emp: any }) {
  const qc = useQueryClient();
  const [commPrefs, setCommPrefs] = useState({ email: true, sms: false, whatsapp: false, push: true });
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  // `emp` is always truthy — my-profile returns a synthetic profile
  // (employee_ref === '') for users with no real employee record. Channel
  // preferences are stored on the employee row, so there is nowhere to persist
  // them without one; gate the editor on a real record.
  const hasEmployeeRecord = !!emp?.employee_ref;

  useEffect(() => {
    if (emp?.comm_preferences) setCommPrefs(emp.comm_preferences);
  }, [emp]);

  const saveMutation = useMutation({
    mutationFn: () => portalApi.updateMyProfile({ comm_preferences: commPrefs }),
    onSuccess: () => {
      setSaved(true);
      setSaveError('');
      // my-profile (the source of comm_preferences) is cached under this key.
      qc.invalidateQueries({ queryKey: ['profile/my-avatar'] });
    },
    onError: (e: any) => {
      setSaved(false);
      setSaveError(extractErrorMessage(e, 'Could not save preferences. Please try again.'));
    },
  });

  return (
    <Grid container spacing={2.5}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 1 }}>Communication Channels</Typography>
            <Typography sx={{ fontSize: TYPE.base, color: 'text.secondary', mb: 2 }}>
              Choose how you receive company communications
            </Typography>
            {[
              { key: 'email', label: 'Email', desc: 'Payslips, approvals, and general updates', icon: '\u2709' },
              { key: 'sms', label: 'SMS', desc: 'Urgent alerts and time-sensitive approvals', icon: '\uD83D\uDCAC' },
              { key: 'whatsapp', label: 'WhatsApp', desc: 'Reminders, schedules, and quick messages', icon: '\uD83D\uDCF1' },
              { key: 'push', label: 'Push Notifications', desc: 'In-app alerts and browser notifications', icon: '\uD83D\uDD14' },
            ].map(ch => (
              <Box key={ch.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: TYPE['2xl'] }}>{ch.icon}</Box>
                  <Box>
                    <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.semibold }}>{ch.label}</Typography>
                    <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>{ch.desc}</Typography>
                  </Box>
                </Box>
                <Switch
                  checked={!!commPrefs[ch.key as keyof typeof commPrefs]}
                  onChange={(_, checked) => { setCommPrefs(p => ({ ...p, [ch.key]: checked })); setSaved(false); setSaveError(''); }}
                  disabled={!hasEmployeeRecord}
                  size="small"
                />
              </Box>
            ))}
            {!hasEmployeeRecord && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Communication preferences are saved to your employee record. Ask an
                administrator to add you as an employee (HR &rarr; Employees) to manage these.
              </Alert>
            )}
            {saved && <Alert severity="success" sx={{ mt: 2 }}>Preferences saved.</Alert>}
            {saveError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSaveError('')}>{saveError}</Alert>}
            <Button variant="contained" size="small" startIcon={<SaveIcon />} sx={{ mt: 2 }}
              disabled={saveMutation.isPending || !hasEmployeeRecord}
              onClick={() => saveMutation.mutate()}>
              Save Preferences
            </Button>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Notification Settings</Typography>
              <Link
                component={RouterLink}
                to="/settings/notification-preferences"
                sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold, textDecoration: 'none' }}
              >
                Manage all →
              </Link>
            </Box>
            <Typography sx={{ fontSize: TYPE.base, color: 'text.secondary', mb: 2 }}>
              Control which events trigger notifications and which channels they use (in-app, email, WhatsApp).
            </Typography>
            <Alert severity="info" sx={{ mt: 1 }}>
              Notification preferences are managed on the dedicated{' '}
              <Link component={RouterLink} to="/settings/notification-preferences" sx={{ fontWeight: TYPE.semibold }}>
                Notification Preferences
              </Link>{' '}
              settings page where changes save in real time.
            </Alert>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// ── Shared row used by Recent Logins (and any future activity views) ──
function LoginHistoryRow({ entry }: { entry: LoginHistoryEntry }) {
  const deviceType = (entry.device_type || '').toLowerCase();
  const DeviceIcon = deviceType === 'mobile' ? PhoneIcon
    : deviceType === 'tablet' ? TabletIcon
    : deviceType === 'desktop' ? LaptopIcon
    : DevicesOtherIcon;
  const deviceLabel = deviceType ? deviceType.charAt(0).toUpperCase() + deviceType.slice(1) : 'Unknown device';

  const isLogout = entry.event_type === 'logout';
  const isFailed = entry.event_type === 'login' && !entry.success;
  const isActiveLogin = entry.event_type === 'login' && entry.success && !entry.logout_at;

  const EventIcon = isLogout ? LogoutIcon : isFailed ? BlockIcon : LoginIcon;
  const eventColor = isLogout ? 'text.secondary' : isFailed ? 'error.main' : 'success.main';
  const eventLabel = isLogout ? 'Logged out' : isFailed ? 'Failed login attempt' : 'Logged in';

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25, position: 'relative' }}>
        <DeviceIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Box sx={{ position: 'absolute', bottom: -3, right: -3, width: 16, height: 16, borderRadius: '50%', bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 1px rgba(0,0,0,0.06)' }}>
          <EventIcon sx={{ fontSize: 11, color: eventColor }} />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.semibold, color: eventColor }}>
            {eventLabel}
          </Typography>
          <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>
            · {entry.browser || 'Unknown browser'}{entry.os ? ` on ${entry.os}` : ''} · {deviceLabel}
          </Typography>
          {isActiveLogin && (
            <Chip
              icon={<DotIcon sx={{ fontSize: 10 }} />}
              label="Active session"
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 20, '& .MuiChip-icon': { color: 'success.main' } }}
            />
          )}
        </Box>
        <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', mt: 0.25 }}>
          {formatDateTime(entry.login_at)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOnOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>
              {entry.location || 'Location unavailable'}
            </Typography>
          </Box>
          {entry.ip_address && (
            <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', fontFamily: 'monospace' }}>
              · {entry.ip_address}
            </Typography>
          )}
          {entry.user_agent && (
            <Tooltip title={entry.user_agent} arrow placement="top">
              <Typography
                sx={{
                  fontSize: TYPE.xs, color: 'text.secondary', fontFamily: 'monospace',
                  maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  cursor: 'help',
                }}
              >
                · {entry.user_agent}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Chip
        label={isLogout ? 'Logout' : isFailed ? 'Failed' : 'Login'}
        size="small"
        color={isLogout ? 'default' : isFailed ? 'error' : 'success'}
        variant="outlined"
        sx={{ height: 20, flexShrink: 0 }}
      />
    </Box>
  );
}

// ── Security Sub-Tab ──
function SecuritySubTab({ user }: { user: any }) {
  const { setUser: setAuthUser } = useAuthStore();
  const [pw, setPw] = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false });
  const [pwMsg, setPwMsg] = useState({ type: '' as 'success' | 'error' | '', text: '' });

  const { data: logins } = useQuery({
    queryKey: ['profile/logins', user?.id],
    queryFn: () => auditApi.loginHistory({ user_id: user!.id, page: 1, per_page: 10 }).then(r => r.data.items),
    enabled: !!user?.id,
  });

  const pwMutation = useMutation({
    mutationFn: () => authApi.changePassword({ current_password: pw.current, new_password: pw.newPw }),
    onSuccess: () => {
      setPwMsg({ type: 'success', text: 'Password changed.' });
      setPw({ current: '', newPw: '', confirm: '' });
      if (user?.must_change_password) {
        const { companies } = useAuthStore.getState();
        setAuthUser({ ...user, must_change_password: false }, companies);
      }
    },
    onError: (e: any) => setPwMsg({ type: 'error', text: extractErrorMessage(e, 'Failed.') }),
  });

  const handlePwSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pw.current || !pw.newPw || !pw.confirm) { setPwMsg({ type: 'error', text: 'All fields required.' }); return; }
    if (pw.newPw.length < 8) { setPwMsg({ type: 'error', text: 'Min 8 characters.' }); return; }
    if (pw.newPw !== pw.confirm) { setPwMsg({ type: 'error', text: "Passwords don't match." }); return; }
    pwMutation.mutate();
  };

  return (
    <Grid container spacing={2.5}>
      {user?.must_change_password && (
        <Grid size={12}>
          <Alert severity="warning" variant="filled">
            You are using a temporary password. Please change it now to secure your account.
          </Alert>
        </Grid>
      )}
      <Grid size={12}>
        <TwoFactorCard user={user} />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 2 }}>Change Password</Typography>
            {pwMsg.text && <Alert severity={pwMsg.type || 'info'} sx={{ mb: 2 }}>{pwMsg.text}</Alert>}
            <Box component="form" onSubmit={handlePwSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Current Password" type={showPw.current ? 'text' : 'password'} value={pw.current}
                onChange={e => setPw(p => ({ ...p, current: e.target.value }))} required
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPw(p => ({ ...p, current: !p.current }))} edge="end" size="small">{showPw.current ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
              <TextField label="New Password" type={showPw.new ? 'text' : 'password'} value={pw.newPw}
                onChange={e => setPw(p => ({ ...p, newPw: e.target.value }))} required helperText="Min 8 chars, 1 uppercase, 1 number, 1 special"
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPw(p => ({ ...p, new: !p.new }))} edge="end" size="small">{showPw.new ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
              <TextField label="Confirm Password" type="password" value={pw.confirm}
                onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} required />
              <Button type="submit" variant="contained" disabled={pwMutation.isPending} sx={{ alignSelf: 'flex-start' }}>
                {pwMutation.isPending ? 'Changing...' : 'Change Password'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Login & Logout Activity</Typography>
              <Link
                component={RouterLink}
                to="/audit?tab=logins"
                sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold, textDecoration: 'none' }}
              >
                View full history →
              </Link>
            </Box>
            {logins && logins.length > 0
              ? logins.map((entry: LoginHistoryEntry) => <LoginHistoryRow key={entry.id} entry={entry} />)
              : <Typography variant="body2" color="text.secondary">No login history.</Typography>}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// ── Two-Factor Authentication Card ──
function TwoFactorCard({ user }: { user: any }) {
  const { setUser } = useAuthStore();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [regenerateCode, setRegenerateCode] = useState('');
  const [enrollData, setEnrollData] = useState<{ secret: string; provisioning_uri: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [remainingCodes, setRemainingCodes] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });

  const isEnabled = Boolean(user?.totp_enabled);

  // Lazy-load remaining recovery code count once when 2FA is enabled
  useEffect(() => {
    if (!isEnabled || remainingCodes !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.recoveryCodeStatus();
        if (!cancelled) setRemainingCodes(res.data.remaining);
      } catch {
        // Non-fatal — count stays hidden
      }
    })();
    return () => { cancelled = true; };
  }, [isEnabled, remainingCodes]);

  const startEnroll = async () => {
    setMsg({ type: '', text: '' });
    try {
      const { data } = await authApi.enroll2FA();
      setEnrollData(data);
      setCode('');
      setRecoveryCodes(null);
      setEnrollOpen(true);
    } catch (e: any) {
      setMsg({ type: 'error', text: extractErrorMessage(e, 'Could not start enrollment.') });
    }
  };

  const confirmMutation = useMutation({
    mutationFn: () => authApi.confirm2FA(code),
    onSuccess: ({ data }) => {
      setRecoveryCodes(data.recovery_codes);
      setRemainingCodes(data.recovery_codes?.length ?? null);
      const { companies, activeCompanyId, platformRole } = useAuthStore.getState();
      setUser({ ...user, totp_enabled: true }, companies, activeCompanyId ?? undefined, platformRole);
      setMsg({ type: 'success', text: 'Two-factor authentication enabled.' });
    },
    onError: (e: any) => setMsg({ type: 'error', text: extractErrorMessage(e, 'Invalid code.') }),
  });

  const disableMutation = useMutation({
    mutationFn: () => authApi.disable2FA(disableCode),
    onSuccess: () => {
      const { companies, activeCompanyId, platformRole } = useAuthStore.getState();
      setUser({ ...user, totp_enabled: false }, companies, activeCompanyId ?? undefined, platformRole);
      setDisableOpen(false);
      setDisableCode('');
      setRemainingCodes(null);
      setMsg({ type: 'success', text: 'Two-factor authentication disabled.' });
    },
    onError: (e: any) => setMsg({ type: 'error', text: extractErrorMessage(e, 'Invalid code.') }),
  });

  const regenerateMutation = useMutation({
    mutationFn: () => authApi.regenerate2FARecoveryCodes(regenerateCode),
    onSuccess: ({ data }) => {
      setRecoveryCodes(data.recovery_codes);
      setRemainingCodes(data.recovery_codes?.length ?? null);
      setRegenerateOpen(false);
      setRegenerateCode('');
      setEnrollOpen(true); // reuse the "Save your recovery codes" panel
      setMsg({ type: 'success', text: 'Recovery codes regenerated. The old set is no longer valid.' });
    },
    onError: (e: any) => setMsg({ type: 'error', text: extractErrorMessage(e, 'Invalid code.') }),
  });

  const copy = (text: string) => navigator.clipboard?.writeText(text);

  const downloadRecoveryCodes = () => {
    if (!recoveryCodes) return;
    const content = [
      'AnooreHR — Two-Factor Authentication Recovery Codes',
      `Account: ${user?.email ?? ''}`,
      `Generated: ${new Date().toISOString()}`,
      '',
      'Each code works exactly once. Store them somewhere safe — password manager, printed copy, etc.',
      '',
      ...recoveryCodes,
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'anoorehr-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{ mt: 0.25, color: isEnabled ? 'success.main' : 'text.secondary' }}>
              {isEnabled ? <VerifiedUserOutlined /> : <ShieldOutlined />}
            </Box>
            <Box>
              <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>
                Two-Factor Authentication
                <Chip
                  label={isEnabled ? 'Enabled' : 'Disabled'}
                  size="small"
                  color={isEnabled ? 'success' : 'default'}
                  sx={{ ml: 1, height: 20, fontWeight: TYPE.semibold }}
                />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
                Add a second verification step using an authenticator app (1Password, Authy, Google
                Authenticator). Required for platform administrators.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEnabled ? (
              <Button variant="outlined" color="error" onClick={() => { setDisableCode(''); setDisableOpen(true); }}>
                Disable 2FA
              </Button>
            ) : (
              <Button variant="contained" onClick={startEnroll}>Enable 2FA</Button>
            )}
          </Box>
        </Box>
        {isEnabled && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.semibold }}>Recovery codes</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: TYPE.sm }}>
                {remainingCodes === null
                  ? 'Use these if you lose access to your authenticator app.'
                  : remainingCodes === 0
                    ? 'No recovery codes left — regenerate a new batch to avoid getting locked out.'
                    : `${remainingCodes} recovery code${remainingCodes === 1 ? '' : 's'} remaining. Each works exactly once.`}
              </Typography>
            </Box>
            {remainingCodes !== null && remainingCodes <= 2 && (
              <Chip
                label={remainingCodes === 0 ? 'Exhausted' : 'Running low'}
                color={remainingCodes === 0 ? 'error' : 'warning'}
                size="small"
              />
            )}
            <Button variant="outlined" size="small" onClick={() => { setRegenerateCode(''); setRegenerateOpen(true); }}>
              Regenerate
            </Button>
          </Box>
        )}
        {msg.text && !enrollOpen && !disableOpen && !regenerateOpen && (
          <Alert severity={msg.type || 'info'} sx={{ mt: 2 }} onClose={() => setMsg({ type: '', text: '' })}>
            {msg.text}
          </Alert>
        )}
      </CardContent>

      {/* Enrollment Dialog */}
      <Dialog open={enrollOpen} onClose={() => setEnrollOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {recoveryCodes ? 'Save your recovery codes' : 'Set up two-factor authentication'}
        </DialogTitle>
        <DialogContent dividers>
          {!recoveryCodes && enrollData && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                1. Scan this QR code with your authenticator app, or enter the secret manually.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <QRCodeSVG value={enrollData.provisioning_uri} size={180} level="M" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <TextField
                  fullWidth size="small" label="Secret" value={enrollData.secret}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                />
                <IconButton onClick={() => copy(enrollData.secret)} size="small"><ContentCopy fontSize="small" /></IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                2. Enter the 6-digit code from your authenticator to confirm.
              </Typography>
              <TextField
                fullWidth autoFocus value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" inputMode="numeric"
                InputProps={{ sx: { letterSpacing: '0.3em', fontWeight: TYPE.bold, fontSize: TYPE.xl, textAlign: 'center' } }}
              />
              {msg.text && <Alert severity={msg.type || 'info'} sx={{ mt: 2 }}>{msg.text}</Alert>}
            </Box>
          )}
          {recoveryCodes && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Store these codes somewhere safe. They are the only way to recover your account if you
                lose your authenticator, and they are shown only once.
              </Alert>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, fontFamily: 'monospace', fontSize: TYPE.md, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {recoveryCodes.map((c) => <Box key={c}>{c}</Box>)}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button
                  size="small" variant="outlined" startIcon={<ContentCopy fontSize="small" />}
                  onClick={() => copy(recoveryCodes.join('\n'))}
                  sx={{ textTransform: 'none' }}
                >
                  Copy
                </Button>
                <Button
                  size="small" variant="outlined"
                  onClick={downloadRecoveryCodes}
                  sx={{ textTransform: 'none' }}
                >
                  Download .txt
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!recoveryCodes ? (
            <>
              <Button onClick={() => setEnrollOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={code.length !== 6 || confirmMutation.isPending}
                onClick={() => confirmMutation.mutate()}
              >
                {confirmMutation.isPending ? 'Verifying...' : 'Enable'}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setEnrollOpen(false)}>Done</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={disableOpen} onClose={() => setDisableOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable two-factor authentication</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the current 6-digit code from your authenticator to confirm.
          </Typography>
          <TextField
            fullWidth autoFocus value={disableCode}
            onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000" inputMode="numeric"
            InputProps={{ sx: { letterSpacing: '0.3em', fontWeight: TYPE.bold, fontSize: TYPE.xl, textAlign: 'center' } }}
          />
          {msg.text && <Alert severity={msg.type || 'info'} sx={{ mt: 2 }}>{msg.text}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableOpen(false)}>Cancel</Button>
          <Button
            variant="contained" color="error"
            disabled={disableCode.length !== 6 || disableMutation.isPending}
            onClick={() => disableMutation.mutate()}
          >
            {disableMutation.isPending ? 'Disabling...' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Dialog */}
      <Dialog open={regenerateOpen} onClose={() => setRegenerateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Regenerate recovery codes</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            The current set of recovery codes will be invalidated. Enter a 6-digit code from your
            authenticator to confirm.
          </Alert>
          <TextField
            fullWidth autoFocus value={regenerateCode}
            onChange={e => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000" inputMode="numeric"
            InputProps={{ sx: { letterSpacing: '0.3em', fontWeight: TYPE.bold, fontSize: TYPE.xl, textAlign: 'center' } }}
          />
          {msg.text && <Alert severity={msg.type || 'info'} sx={{ mt: 2 }}>{msg.text}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={regenerateCode.length !== 6 || regenerateMutation.isPending}
            onClick={() => regenerateMutation.mutate()}
          >
            {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ── Preferences Sub-Tab ──

const HOMEPAGE_OPTIONS = [
  { value: '', label: 'Use company default' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'portal', label: 'Employee Portal' },
  { value: 'profile', label: 'My Profile' },
  { value: 'module', label: 'Module Page' },
];

const MODULE_OPTIONS = [
  { value: 'accounting', label: 'Finance' },
  { value: 'hr', label: 'People' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'tenants', label: 'Tenants' },
  { value: 'budget', label: 'Budget' },
  { value: 'group', label: 'Group' },
  { value: 'recruitment', label: 'Recruitment' },
  { value: 'performance', label: 'Performance' },
  { value: 'settings', label: 'Settings' },
];

function PreferencesSubTab() {
  const { showPageHelp, setShowPageHelp } = useUIStore();
  const { showSuccess, showError } = useNotificationStore();
  const { user } = useAuthStore();

  const prefs = (user as any)?.preferences ?? {};
  const [homepageType, setHomepageType] = useState(prefs.default_homepage || '');
  const [moduleValue, setModuleValue] = useState(prefs.default_module || 'accounting');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setHomepageType(prefs.default_homepage || '');
    setModuleValue(prefs.default_module || 'accounting');
  }, [prefs.default_homepage, prefs.default_module]);

  const prefsMutation = useMutation({
    mutationFn: (payload: { default_homepage?: string; default_module?: string }) =>
      authApi.updatePreferences(payload),
    onSuccess: (res: any) => {
      const updated = res.data?.preferences ?? {};
      const current = useAuthStore.getState().user;
      if (current) {
        useAuthStore.setState({ user: { ...current, preferences: updated } });
      }
      showSuccess('Homepage preference saved.');
      setDirty(false);
    },
    onError: (err: any) => {
      showError(extractErrorMessage(err, 'Failed to save preference.'));
    },
  });

  const handleSave = () => {
    const payload: { default_homepage?: string; default_module?: string } = {
      default_homepage: homepageType,
    };
    if (homepageType === 'module') {
      payload.default_module = moduleValue;
    }
    prefsMutation.mutate(payload);
  };

  return (
    <Grid container spacing={2.5}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 2 }}>Display Preferences</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.semibold }}>Page Help Icons</Typography>
                <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>Show contextual help icons on pages</Typography>
              </Box>
              <Switch checked={showPageHelp} onChange={(_, v) => setShowPageHelp(v)} size="small" />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 2 }}>Default Homepage</Typography>
            <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', mb: 2 }}>
              Choose which page you see first after logging in. Leave as "Use company default" to follow your company's setting.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={homepageType}
                  onChange={e => { setHomepageType(e.target.value); setDirty(true); }}
                  displayEmpty
                >
                  {HOMEPAGE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {homepageType === 'module' && (
                <FormControl fullWidth size="small">
                  <Select
                    value={moduleValue}
                    onChange={e => { setModuleValue(e.target.value); setDirty(true); }}
                  >
                    {MODULE_OPTIONS.map(mod => (
                      <MenuItem key={mod.value} value={mod.value}>{mod.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {dirty && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  disabled={prefsMutation.isPending}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {prefsMutation.isPending ? 'Saving...' : 'Save Preference'}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
